import useEventCallback from '@unifyapps/hooks/useEventCallback';
import type { LogoutArgs } from '@unifyapps/network/utils/fetch/types';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';

// Web placeholder. Auth state on web is tracked via cookie + user store
// presence; this context is a no-op shim so shared carbon code can call
// `useAuth()` without platform-splitting every import site. See the
// native counterpart `AuthContext.native.tsx` for the real
// implementation (boolean `isAuthenticated`, `checkAuth`, `logout`).

type AuthContextType = {
  isAuthenticated: boolean;
  checkAuth: () => Promise<void>;
  logout: (args: LogoutArgs) => Promise<void>;
  resetForInterfaceSwitch: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: true,
  checkAuth: async () => Promise.resolve(),
  logout: async () => Promise.resolve(),
  resetForInterfaceSwitch: async () => Promise.resolve(),
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const checkAuth = useEventCallback<AuthContextType['checkAuth']>(async () => {
    // placeholder for web.
  });

  const logout = useEventCallback(async (_args: LogoutArgs): Promise<void> => {
    // placeholder for web.
  });

  const resetForInterfaceSwitch = useEventCallback(async (): Promise<void> => {
    // placeholder for web.
  });

  const contextValue = useMemo(
    () => ({
      isAuthenticated: true,
      checkAuth,
      logout,
      resetForInterfaceSwitch,
    }),
    [checkAuth, logout, resetForInterfaceSwitch],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
